import { useState } from 'react';
import { useInView } from '../lib/useInView';
import {
  ClipboardList,
  Sparkles,
  Users,
  Briefcase,
  Rocket,
  Wrench,
  GraduationCap,
  Smartphone,
  FileText,
  Calendar,
  LayoutTemplate,
  Check,
  ChevronDown,
  Play,
  ArrowRight,
  Image as ImageIcon,
  KanbanSquare,
} from 'lucide-react';
import { useTranslation } from '../lib/LanguageContext';
import type { TranslationKey } from '../lib/locales/types';

interface LandingContentProps {
  onLoginClick: () => void;
}

export function LandingContent({ onLoginClick }: LandingContentProps) {
  const { t } = useTranslation();

  const scrollToId = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex-1 flex flex-col">
      <Hero onLoginClick={onLoginClick} onSecondaryClick={() => scrollToId('how-it-works')} t={t} />
      <UseCases t={t} />
      <ZeroLearningCurve t={t} />
      <MobileSection t={t} />
      <FeatureGrid t={t} />
      <Showcase t={t} />
      <Pricing onLoginClick={onLoginClick} t={t} />
      <FAQ t={t} />
      <FinalCta onLoginClick={onLoginClick} t={t} />
    </div>
  );
}

type T = (key: TranslationKey) => string;

function Hero({ onLoginClick, onSecondaryClick, t }: { onLoginClick: () => void; onSecondaryClick: () => void; t: T }) {
  return (
    <section className="relative overflow-hidden px-6 md:px-12 py-16 md:py-24 text-center bg-gradient-to-br from-purple-200 via-indigo-200 to-blue-200 min-h-[calc(100vh-4rem)] flex items-center justify-center">
      {/* Animated backdrop — isolated to the hero */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute -top-32 -right-32 w-[540px] h-[540px] bg-gradient-to-br from-indigo-500/60 to-purple-500/60 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '9s' }}
        ></div>
        <div
          className="absolute -bottom-40 -left-32 w-[480px] h-[480px] bg-gradient-to-tr from-purple-500/55 to-indigo-500/55 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '11s', animationDelay: '2s' }}
        ></div>
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[320px] h-[320px] bg-gradient-to-br from-blue-400/40 to-indigo-400/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '13s', animationDelay: '4s' }}
        ></div>
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #6366f1 1.25px, transparent 0)`,
            backgroundSize: '44px 44px',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white"></div>
      </div>

      <div className="relative max-w-4xl mx-auto">
        <div className="hero-anim hero-anim-d1 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur-sm text-indigo-700 text-xs md:text-sm font-medium border border-indigo-200/60 shadow-sm mb-8">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          {t('landingHeroBadge')}
        </div>

        <div className="hero-anim hero-anim-d2 flex items-center justify-center mb-6">
          <ClipboardList className="h-10 w-10 md:h-12 md:w-12 text-indigo-600 mr-3" />
          <span className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Simple Stickers</span>
        </div>

        <h1 className="hero-anim hero-anim-d2 font-bold tracking-tight leading-[1.05] mb-6">
          {t('landingHeroHeadline')
            .split(/\.\s+/)
            .filter(Boolean)
            .map((part, i, arr) => (
              <span key={i} className="block">
                {i === 0 ? (
                  <span className="text-[2.6rem] md:text-7xl lg:text-8xl text-indigo-600">
                    {part}{i < arr.length - 1 || /\.$/.test(t('landingHeroHeadline')) ? '.' : ''}
                  </span>
                ) : (
                  <span className="text-4xl md:text-6xl lg:text-7xl text-gray-900">
                    {part}{i < arr.length - 1 || /\.$/.test(t('landingHeroHeadline')) ? '.' : ''}
                  </span>
                )}
              </span>
            ))}
        </h1>

        <p className="hero-anim hero-anim-d3 text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
          {t('landingHeroSubheadline')}
        </p>

        <div className="hero-anim hero-anim-d4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onLoginClick}
            className="group inline-flex items-center gap-2 bg-indigo-600 text-white px-7 py-3.5 rounded-xl text-base md:text-lg font-semibold hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 transition-all duration-200 shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/50"
          >
            {t('landingCtaPrimary')}
            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
          <button
            onClick={onSecondaryClick}
            className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-800 px-7 py-3.5 rounded-xl text-base md:text-lg font-semibold border border-gray-200 hover:bg-white hover:border-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 transition-colors duration-200"
          >
            <Play className="h-4 w-4 text-indigo-600" />
            {t('landingCtaSecondary')}
          </button>
        </div>
      </div>
    </section>
  );
}

function UseCases({ t }: { t: T }) {
  const { ref, inView } = useInView();
  const items: Array<{ Icon: typeof Briefcase; titleKey: TranslationKey; descKey: TranslationKey; accent: string }> = [
    { Icon: Briefcase, titleKey: 'useCaseWorkTitle', descKey: 'useCaseWorkDesc', accent: 'from-indigo-500 to-blue-500' },
    { Icon: Rocket, titleKey: 'useCaseFreelanceTitle', descKey: 'useCaseFreelanceDesc', accent: 'from-purple-500 to-indigo-500' },
    { Icon: Wrench, titleKey: 'useCaseHobbyTitle', descKey: 'useCaseHobbyDesc', accent: 'from-amber-500 to-orange-500' },
    { Icon: GraduationCap, titleKey: 'useCaseStudyTitle', descKey: 'useCaseStudyDesc', accent: 'from-emerald-500 to-teal-500' },
  ];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="px-6 md:px-12 py-14 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto">
        <SectionHeader title={t('landingForEveryoneTitle')} subtitle={t('landingForEveryoneSubtitle')} inView={inView} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map(({ Icon, titleKey, descKey, accent }, idx) => (
            <div
              key={titleKey}
              className={`land-anim land-anim-d${idx + 1} ${inView ? 'in-view' : ''} group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/60 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200`}
            >
              <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br ${accent} text-white mb-4 shadow-sm`}>
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1.5">{t(titleKey)}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{t(descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ZeroLearningCurve({ t }: { t: T }) {
  const { ref, inView } = useInView();
  const steps: Array<{ num: string; titleKey: TranslationKey; descKey: TranslationKey }> = [
    { num: '1', titleKey: 'stepCreateProject', descKey: 'stepCreateProjectDesc' },
    { num: '2', titleKey: 'stepAddStickyNote', descKey: 'stepAddStickyNoteDesc' },
    { num: '3', titleKey: 'stepDragToDone', descKey: 'stepDragToDoneDesc' },
  ];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} id="how-it-works" className="px-6 md:px-12 py-14 md:py-20 bg-gray-50 scroll-mt-24">
      <div className="max-w-6xl mx-auto">
        <SectionHeader title={t('landingInstantTitle')} subtitle={t('landingInstantSubtitle')} inView={inView} />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-2 space-y-4">
            {steps.map(({ num, titleKey, descKey }, idx) => (
              <div key={num} className={`land-anim land-anim-d${idx + 1} ${inView ? 'in-view' : ''} flex items-start gap-4`}>
                <div className="flex-shrink-0 h-11 w-11 rounded-full bg-indigo-600 text-white font-bold grid place-items-center shadow-md shadow-indigo-600/30">
                  {num}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t(titleKey)}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{t(descKey)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className={`land-anim land-anim-d2 ${inView ? 'in-view' : ''} lg:col-span-3`}>
            <img
              src="/SimpleStickersApp.webp"
              alt={t('screenshotKanbanLabel')}
              className="w-full rounded-2xl shadow-xl border border-gray-200/60"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileSection({ t }: { t: T }) {
  const { ref, inView } = useInView();
  const bullets: TranslationKey[] = ['mobileBulletPwa', 'mobileBulletDrag', 'mobileBulletSync'];
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="px-6 md:px-12 py-14 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className={`land-anim land-anim-d1 ${inView ? 'in-view' : ''}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium mb-4">
            <Smartphone className="h-3.5 w-3.5" />
            Mobile
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">{t('landingMobileTitle')}</h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">{t('landingMobileSubtitle')}</p>
          <ul className="space-y-3 mb-6">
            {bullets.map((key) => (
              <li key={key} className="flex items-start gap-3">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mt-0.5">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-gray-700">{t(key)}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-5 py-4 space-y-2">
            <p className="text-sm font-semibold text-indigo-800">{t('pwaInstallTitle')}</p>
            <p className="text-sm text-indigo-700">{t('pwaInstallIosSteps')}</p>
            <p className="text-sm text-indigo-700">{t('pwaInstallAndroidSteps')}</p>
          </div>
        </div>
        <div className={`land-anim land-anim-d2 ${inView ? 'in-view' : ''} flex justify-center`}>
          <div className="relative">
            <div className="w-64 md:w-72 rounded-[2.5rem] bg-gray-900 p-3 shadow-2xl">
              <div className="absolute top-3 left-1/2 -translate-x-1/2 h-5 w-24 bg-gray-900 rounded-b-2xl z-10"></div>
              <div className="rounded-[2rem] overflow-hidden border border-gray-800">
                <img
                  src="/SimpleStickersAppMobile.webp"
                  alt={t('screenshotMobileLabel')}
                  className="w-full block"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureGrid({ t }: { t: T }) {
  const { ref, inView } = useInView();
  const features: Array<{ Icon: typeof Sparkles; titleKey: TranslationKey; descKey: TranslationKey }> = [
    { Icon: KanbanSquare, titleKey: 'featureVisualTitle', descKey: 'featureVisualDesc' },
    { Icon: FileText, titleKey: 'featureNotesTitle', descKey: 'featureNotesDesc' },
    { Icon: Sparkles, titleKey: 'featureAiTitle', descKey: 'featureAiDesc' },
    { Icon: LayoutTemplate, titleKey: 'featureTemplatesTitle', descKey: 'featureTemplatesDesc' },
    { Icon: Calendar, titleKey: 'featureDeadlinesTitle', descKey: 'featureDeadlinesDesc' },
    { Icon: Users, titleKey: 'featureCollabTitle', descKey: 'featureCollabDesc' },
  ];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="px-6 md:px-12 py-14 md:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map(({ Icon, titleKey, descKey }, idx) => (
            <div
              key={titleKey}
              className={`land-anim land-anim-d${(idx % 3) + 1} ${inView ? 'in-view' : ''} bg-white rounded-2xl p-6 border border-gray-200/60 shadow-sm hover:shadow-md transition-shadow duration-200`}
            >
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-100 text-indigo-600 mb-4">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1.5">{t(titleKey)}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{t(descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Showcase({ t }: { t: T }) {
  const { ref, inView } = useInView();
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="px-6 md:px-12 py-14 md:py-20 bg-white">
      <div className="max-w-5xl mx-auto">
        <SectionHeader title={t('landingShowcaseTitle')} subtitle={t('landingShowcaseSubtitle')} inView={inView} />

        <div className={`land-anim land-anim-d1 ${inView ? 'in-view' : ''} rounded-2xl overflow-hidden shadow-2xl border border-gray-200/60 bg-gray-900`}>
          <div className="aspect-video">
            <iframe
              src="https://www.youtube.com/embed/Gtsevlg91v0"
              title="Simple Stickers Intro Video"
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ onLoginClick, t }: { onLoginClick: () => void; t: T }) {
  const { ref, inView } = useInView();
  const freeFeatures: TranslationKey[] = [
    'pricingFreeFeature1',
    'pricingFreeFeature2',
    'pricingFreeFeature3',
    'pricingFreeFeature4',
    'pricingFreeFeature5',
  ];
  const premiumFeatures: TranslationKey[] = [
    'pricingPremiumFeature1',
    'pricingPremiumFeature2',
    'pricingPremiumFeature3',
    'pricingPremiumFeature4',
    'pricingPremiumFeature5',
  ];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} id="pricing" className="px-6 md:px-12 py-14 md:py-20 bg-gray-50 scroll-mt-24">
      <div className="max-w-5xl mx-auto">
        <SectionHeader title={t('pricingTitle')} subtitle={t('pricingSubtitle')} inView={inView} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Free */}
          <div className={`land-anim land-anim-d1 ${inView ? 'in-view' : ''} bg-white rounded-2xl p-8 border border-gray-200/70 shadow-sm flex flex-col`}>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{t('pricingFreeTitle')}</h3>
            <p className="text-sm text-gray-500 mb-6">{t('pricingFreeTagline')}</p>
            <div className="mb-6">
              <span className="text-5xl font-bold text-gray-900">{t('pricingFreePrice')}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {freeFeatures.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-100 text-gray-600 grid place-items-center mt-0.5">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-sm text-gray-700">{t(key)}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={onLoginClick}
              className="w-full bg-gray-900 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 transition-colors duration-200"
            >
              {t('landingCtaPrimary')}
            </button>
          </div>

          {/* Premium */}
          <div className={`land-anim land-anim-d2 ${inView ? 'in-view' : ''} relative bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 shadow-xl shadow-indigo-600/30 flex flex-col text-white`}>
            <span className="absolute -top-3 right-6 bg-amber-400 text-amber-900 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
              {t('pricingMostPopular')}
            </span>
            <h3 className="text-xl font-bold mb-1">{t('pricingPremiumTitle')}</h3>
            <p className="text-sm text-indigo-100 mb-6">{t('pricingPremiumTagline')}</p>
            <div className="mb-6">
              <span className="text-5xl font-bold">{t('pricingPremiumPrice')}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {premiumFeatures.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-5 w-5 rounded-full bg-white/20 text-white grid place-items-center mt-0.5">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-sm text-indigo-50">{t(key)}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={onLoginClick}
              className="w-full bg-white text-indigo-700 px-6 py-3 rounded-xl text-sm font-semibold hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-600 transition-colors duration-200"
            >
              {t('landingCtaPrimary')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ({ t }: { t: T }) {
  const items: Array<{ q: TranslationKey; a: TranslationKey }> = [
    { q: 'faqQ1', a: 'faqA1' },
    { q: 'faqQ2', a: 'faqA2' },
    { q: 'faqQ3', a: 'faqA3' },
    { q: 'faqQ4', a: 'faqA4' },
    { q: 'faqQ5', a: 'faqA5' },
    { q: 'faqQ6', a: 'faqA6' },
  ];
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section id="faq" className="px-6 md:px-12 py-14 md:py-20 bg-white scroll-mt-24">
      <div className="max-w-3xl mx-auto">
        <SectionHeader title={t('faqTitle')} subtitle={t('faqSubtitle')} />
        <div className="space-y-3">
          {items.map(({ q, a }, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={q}
                className="bg-white rounded-xl border border-gray-200/70 shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
                  aria-expanded={isOpen}
                >
                  <span className="text-base font-semibold text-gray-900">{t(q)}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{t(a)}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onLoginClick, t }: { onLoginClick: () => void; t: T }) {
  const { ref, inView } = useInView();
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="px-6 md:px-12 py-14 md:py-24 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className={`land-anim land-anim-d1 ${inView ? 'in-view' : ''} relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 px-8 py-14 md:py-20 text-center shadow-2xl shadow-indigo-600/30`}>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}></div>
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
              {t('finalCtaTitle')}
            </h2>
            <p className="text-base md:text-lg text-indigo-100 max-w-xl mx-auto mb-8">
              {t('finalCtaSubtitle')}
            </p>
            <button
              onClick={onLoginClick}
              className="group inline-flex items-center gap-2 bg-white text-indigo-700 px-8 py-4 rounded-xl text-base md:text-lg font-semibold hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-700 transition-colors duration-200 shadow-lg"
            >
              {t('landingCtaPrimary')}
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ title, subtitle, inView = true }: { title: string; subtitle: string; inView?: boolean }) {
  return (
    <div className={`land-anim ${inView ? 'in-view' : ''} text-center max-w-2xl mx-auto mb-10 md:mb-14`}>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-3">{title}</h2>
      <p className="text-base md:text-lg text-gray-600 leading-relaxed">{subtitle}</p>
    </div>
  );
}

function ScreenshotPlaceholder({ label, tall = false }: { label: string; tall?: boolean }) {
  return (
    <div
      className={`w-full ${tall ? 'aspect-[16/10]' : 'aspect-video'} rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border-2 border-dashed border-indigo-300/70 grid place-items-center text-center p-8 shadow-inner`}
      role="img"
      aria-label={label}
    >
      <div>
        <ImageIcon className="h-12 w-12 text-indigo-400 mx-auto mb-3" />
        <p className="text-xs text-indigo-500 uppercase tracking-wider mb-1 font-medium">Screenshot placeholder</p>
        <p className="text-sm md:text-base text-gray-700 font-medium max-w-sm mx-auto">{label}</p>
      </div>
    </div>
  );
}
